import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { defineStore } from 'pinia'

import { DocumentClass } from '@types'

import { duplicateBunchElement, isDirtyList, unsavedGridBlocker, createBunchElement, unshiftBunchElement } from '../utils/helpers'

import { ROUTE_NAME } from '@/const'

import { useMainStore } from '@/store/main'
import { useDocumentsStore } from './'
import { useRepositoriesStore } from '../repositories/index'

export const useDocumentsBunchStore = defineStore('documents-bunch', () => {
  // INIT

  const mainStore = useMainStore()
  const documentsStore = useDocumentsStore()
  const repositoryStore = useRepositoriesStore()

  const router = useRouter()

  const documentsList = computed(() => documentsStore.getList)

  // STORE

  const initFlag = ref(false)
  const list = ref<Map<string, DocumentClass>>(new Map())

  // ACTIONS

  const createElement = () => createBunchElement(list, DocumentClass, {
    repository_id: repositoryStore.getCurrentId,
  })

  const unshiftElement = (element: DocumentClass) => unshiftBunchElement(element, list)

  const duplicateElement = (id: string) => duplicateBunchElement(id, list, DocumentClass)

  // GETTERS

  const getList = computed(() => (list.value.size ? list.value : new Map()))

  const getElementById = (id: string) => list.value.get(id) as DocumentClass

  const isDirty = computed(() => isDirtyList(list.value))

  // FILL STORE

  watch(documentsList, value => {
    const isFirstLoading = list.value.size === 0
    value.forEach(item => {
      if (!list.value.get(item.id)) {
        const instance = new DocumentClass(item)
        if (isFirstLoading) {
          list.value.set(item.id, instance)
        } else {
          unshiftElement(instance)
        }
        instance.validateData()
      }
    })
    initFlag.value = true
  })

  // ADD BLOCKER IF DIRTY

  watch(isDirty, value => {
    if (value) {
      mainStore.addBlocker(
        'documentsHasChanges',
        unsavedGridBlocker('documents', () =>
          router.push({ name: ROUTE_NAME.DOCUMENTS }),
        ),
      )
    } else {
      mainStore.removeBlocker('documentsHasChanges')
    }
  })

  const validateAll = () => {
    list.value.forEach(item => {
      item.validateData()
    })
  }

  // CLEAR STORE

  const clear = () => {
    initFlag.value = false
    list.value = new Map()
  }

  return {
    initFlag,

    createElement,
    unshiftElement,
    duplicateElement,

    getList,
    getElementById,

    isDirty,

    validateAll,

    clear,
  }
})
